/**
 * Client-side commodity helper. Fetches /api/commodities once, caches in module
 * scope + localStorage, and exposes utilities that every commodity-aware
 * component can call synchronously after preload.
 *
 *   await preloadCommodities();          // once on app boot
 *   getCommodity('Au')                    // -> {code, name, grade_unit, ...}
 *   listCommodities()                     // -> [...]
 *   gradeUnit(project.commodity)          // -> '%' | 'g/t' | 'ppm' | 'kcal/kg'
 */
import api from "@/lib/api";

const CACHE_KEY = "smin_commodities_v2";
let CACHE = null;

// Sensible fallback so early renders don't blow up before /commodities lands.
const FALLBACK = [{
  code: "Cu", name: "Copper", grade_unit: "%", cutoff_grade: 0.3,
  revenue_per_grade_unit: 200, mining_cost: 5, processing_cost: 15,
  color: "#FF7F50", hint: "Porphyry, VMS, sedimentary",
}];

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : null;
  } catch { return null; }
}

export async function preloadCommodities() {
  if (CACHE) return CACHE;
  const cached = loadFromStorage();
  if (cached) { CACHE = cached; }
  try {
    const { data } = await api.get("/commodities");
    if (Array.isArray(data.commodities) && data.commodities.length) {
      CACHE = data.commodities;
      localStorage.setItem(CACHE_KEY, JSON.stringify(CACHE));
    }
  } catch {
    if (!CACHE) CACHE = FALLBACK;
  }
  return CACHE;
}

export function listCommodities() {
  return CACHE || loadFromStorage() || FALLBACK;
}

export function getCommodity(code) {
  const list = listCommodities();
  if (!code) return list[0];
  const found = list.find((c) => c.code.toLowerCase() === String(code).toLowerCase());
  return found || list[0];
}

export function gradeUnit(code) {
  return getCommodity(code).grade_unit;
}

export function defaultCutoff(code) {
  return getCommodity(code).cutoff_grade;
}

/** Distinct commodity groups in display order (as they appear in the catalogue). */
export function groups() {
  const seen = [];
  for (const c of listCommodities()) {
    const g = c.group || "Other";
    if (!seen.includes(g)) seen.push(g);
  }
  return seen;
}

/** All commodities in a given group. */
export function inGroup(group) {
  return listCommodities().filter((c) => (c.group || "Other") === group);
}

/**
 * Grade-unit options allowed for the manual per-project override.
 * INTENTIONALLY NOT keyed by commodity — this is a pure enum of units
 * a lab can report in. Downstream ranges/sliders/colour scales must
 * always auto-range from the real project data (per the Feb-2026 spec,
 * Step 3 of the commodity-selector work).
 */
export const GRADE_UNIT_OPTIONS = [
  "%", "g/t", "ppm", "oz/ton", "lb/ton", "ct/t", "kcal/kg",
];
